import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import {
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
} from 'firebase/auth'
import {
  setFirebaseUser,
  setNeedsRegistration,
} from '../../features/FirebaseAuthSlice.ts'
import {
  useClubAdminCreateCheckoutSessionMutation,
  useCreateClubMutation,
  useUpdateUserMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { clearSignUpDraft, loadSignUpDraft } from '../../utils/SignUpDraft.ts'
import SignInLoadingSpinner from '../../components/login/SignInLoadingSpinner.tsx'
import GenericErrorPage from '../GenericErrorPage.tsx'

/**
 * Shared completion route for the passwordless email-link flow. The link in the
 * email navigates here; we finish the Firebase sign-in, materialize the backend
 * user, and resume whatever intent was stashed before the email round-trip
 * (creating a club for the register-club flow, or simply landing the user for a
 * plain sign-up).
 */
export default function CompleteSignUp() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const effectRan = useRef(false)

  const [hasError, setHasError] = useState(false)
  // The error fallback should send the user back to whichever flow they came
  // from; the draft tells us which one.
  const [backToClub, setBackToClub] = useState(false)

  const [updateUser] = useUpdateUserMutation()
  const [createClub] = useCreateClubMutation()
  const [createCheckoutSession] = useClubAdminCreateCheckoutSessionMutation()

  useEffect(() => {
    if (effectRan.current) return
    effectRan.current = true
    void completeSignUp()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const completeSignUp = async () => {
    const auth = getAuth()

    const fail = () => {
      dispatch(setNeedsRegistration(false))
      setHasError(true)
    }

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setHasError(true)
      return
    }

    const draft = loadSignUpDraft()
    if (!draft || !draft.email) {
      setHasError(true)
      return
    }
    setBackToClub(!!draft.club)

    // Suppress any GET /user until the backend user exists.
    dispatch(setNeedsRegistration(true))

    try {
      const credential = await signInWithEmailLink(
        auth,
        draft.email,
        window.location.href
      )
      // Persist the display name so the refreshed token carries a `name` claim
      // for PUT /user.
      if (draft.displayName) {
        await updateProfile(credential.user, { displayName: draft.displayName })
      }
      const token = await credential.user.getIdToken(true)
      dispatch(
        setFirebaseUser({ idpId: credential.user.uid, accessToken: token })
      )
      await updateUser().unwrap()

      // Register-club stashes a club to provision; standalone sign-up does not.
      if (draft.club) {
        const response = await createClub(draft.club).unwrap()
        dispatch(setNeedsRegistration(false))
        clearSignUpDraft()
        // Same hand-off as RegisterClub: card right after club creation. A
        // checkout failure must not surface the sign-up error page — the club
        // exists, so fall back to the admin panel and let the paywall recover.
        try {
          const { url } = await createCheckoutSession(
            String(response.clubId)
          ).unwrap()
          window.location.assign(url)
        } catch {
          navigate(`/club/${response.clubId}/admin`)
        }
        return
      }

      dispatch(setNeedsRegistration(false))
      clearSignUpDraft()
      navigate(draft.next || '/clubs')
    } catch {
      fail()
    }
  }

  if (hasError) {
    return (
      <GenericErrorPage
        title="Sign-Up Failed"
        description="An error occurred while completing your sign-up, or the link you used is invalid, expired, or already used. Please request a new link or try signing up another way.">
        <NavLink
          to={backToClub ? '/register-club' : '/sign-up'}
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          {backToClub ? 'Back to Start your club' : 'Back to sign up'}
        </NavLink>
      </GenericErrorPage>
    )
  }

  return <SignInLoadingSpinner message="Finishing your sign-up, please wait..." />
}
